// Appraisal.jsx - The bench tool. Somebody with a scale and an acid kit types
// what they measure and watches the offer build as they go.
//
// Only four values are ever typed: metal, purity, gross weight, stone weight.
// Everything else, including the fine weight, the rate and the amount, is worked
// out on the server so the customer facing number and the internal number can
// never drift apart.
//
// LEGEND
//   1. lotControls / LotRow / LotCard   one weighed lot, as a row or as a card
//   2. Appraisal                        the table, the running total, and the offer

import { useCallback, useEffect, useMemo, useState } from "react";

import { useAsync, useDebounced } from "../lib/hooks.js";
import { ordersApi, publicApi } from "../lib/api.js";
import { money, moneyExact, grams, METAL_LABELS, TEST_LABELS } from "../lib/format.js";
import { Button, Input, Panel, Alert, Textarea, useToast, Modal } from "../ui/kit.jsx";
import { Plus, Trash, Scale, Check, Refresh } from "../ui/icons.jsx";

const blankLot = () => ({
  label: "", metal: "gold", purity: "14k", grossWeight: "", stoneWeight: "", testMethod: "acid", note: "",
});

////////// 1. ONE LOT //////////
// The controls are defined once and arranged twice: as a row of cells on a wide
// screen, and as a card on a narrow one. Somebody at the bench with a phone in
// one hand and a loupe in the other gets the same four inputs either way.
//
// A ten column table does not survive a 390 pixel screen. Shrinking it would
// make the weight fields, the two that get typed into constantly, about forty
// pixels wide.

function lotControls(lot, onChange, purities) {
  const set = (patch) => onChange({ ...lot, ...patch });
  const options = purities?.[lot.metal] || [];
  const field = "field !py-1.5 !px-2 text-[13px]";

  return {
    label: (
      <input
        className={field}
        placeholder="Two chains"
        value={lot.label}
        onChange={(event) => set({ label: event.target.value })}
        aria-label="Description"
      />
    ),
    metal: (
      <select
        className={`${field} cursor-pointer`}
        value={lot.metal}
        onChange={(event) => set({ metal: event.target.value, purity: "" })}
        aria-label="Metal"
      >
        {Object.entries(METAL_LABELS).map(([value, text]) => <option key={value} value={value}>{text}</option>)}
      </select>
    ),
    purity: (
      <select
        className={`${field} cursor-pointer`}
        value={lot.purity}
        onChange={(event) => set({ purity: event.target.value })}
        aria-label="Purity"
      >
        <option value="">Select</option>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    ),
    grossWeight: (
      <input
        className={`${field} num text-right`}
        type="number" step="0.01" min="0" inputMode="decimal" placeholder="0.00"
        value={lot.grossWeight}
        onChange={(event) => set({ grossWeight: event.target.value })}
        aria-label="Gross weight in grams"
      />
    ),
    stoneWeight: (
      <input
        className={`${field} num text-right`}
        type="number" step="0.01" min="0" inputMode="decimal" placeholder="0.00"
        value={lot.stoneWeight}
        onChange={(event) => set({ stoneWeight: event.target.value })}
        aria-label="Stone weight in grams"
      />
    ),
    testMethod: (
      <select
        className={`${field} cursor-pointer`}
        value={lot.testMethod}
        onChange={(event) => set({ testMethod: event.target.value })}
        aria-label="Test method"
      >
        {Object.entries(TEST_LABELS).map(([value, text]) => <option key={value} value={value}>{text}</option>)}
      </select>
    ),
  };
}

// What the server worked out. Never typed, so never an input.
const lotResults = (priced) => ({
  fine: priced ? grams(priced.fineWeight, 3) : "—",
  rate: priced?.pricePerGram ? `${moneyExact(priced.pricePerGram)}/g` : "—",
  amount: priced ? money(priced.amount) : "—",
});

function LotRow({ lot, priced, purities, onChange, onRemove }) {
  const control = lotControls(lot, onChange, purities);
  const result = lotResults(priced);

  return (
    <tr className="border-b border-rule-soft align-top">
      <td className="p-1.5">{control.label}</td>
      <td className="p-1.5 w-[110px]">{control.metal}</td>
      <td className="p-1.5 w-[130px]">{control.purity}</td>
      <td className="p-1.5 w-[90px]">{control.grossWeight}</td>
      <td className="p-1.5 w-[90px]">{control.stoneWeight}</td>
      <td className="p-1.5 w-[120px]">{control.testMethod}</td>
      <td className="p-1.5 text-right num text-[13px] text-muted whitespace-nowrap">{result.fine}</td>
      <td className="p-1.5 text-right num text-[13px] text-faint whitespace-nowrap">{result.rate}</td>
      <td className="p-1.5 text-right num text-[14px] font-semibold text-ink whitespace-nowrap">{result.amount}</td>
      <td className="p-1.5 w-8">
        <button type="button" onClick={onRemove} className="btn btn-ghost !px-1" aria-label="Remove lot">
          <Trash size={14} />
        </button>
      </td>
    </tr>
  );
}

const CardField = ({ label, children, className }) => (
  <label className={className}>
    <span className="block text-[11px] font-semibold tracking-[0.09em] uppercase text-faint mb-1">{label}</span>
    {children}
  </label>
);

function LotCard({ lot, priced, purities, index, onChange, onRemove }) {
  const control = lotControls(lot, onChange, purities);
  const result = lotResults(priced);

  return (
    <li className="panel p-3.5">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="eyebrow">Lot {index + 1}</p>
        <button type="button" onClick={onRemove} className="btn btn-ghost !px-2 -my-1" aria-label={`Remove lot ${index + 1}`}>
          <Trash size={15} />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <CardField label="Description" className="col-span-2">{control.label}</CardField>
        <CardField label="Metal">{control.metal}</CardField>
        <CardField label="Purity">{control.purity}</CardField>
        <CardField label="Gross g">{control.grossWeight}</CardField>
        <CardField label="Stones g">{control.stoneWeight}</CardField>
        <CardField label="Test" className="col-span-2">{control.testMethod}</CardField>
      </div>

      {/* The three worked out figures, in the same order as the table's last
          three columns, so somebody who learned one layout can read the other. */}
      <div className="grid grid-cols-3 gap-3 mt-3.5 pt-3 border-t border-rule-soft">
        {[["Fine", result.fine, "text-muted"], ["Rate", result.rate, "text-faint"], ["Amount", result.amount, "text-ink font-semibold"]].map(([label, value, tone], position) => (
          <div key={label} className={position === 2 ? "text-right" : ""}>
            <p className="text-[11px] font-semibold tracking-[0.09em] uppercase text-faint">{label}</p>
            <p className={`num text-[13.5px] mt-0.5 ${tone}`}>{value}</p>
          </div>
        ))}
      </div>
    </li>
  );
}

////////// 2. APPRAISAL //////////

export default function Appraisal({ order, onSaved }) {
  const toast = useToast();
  const { data: settings } = useAsync(() => publicApi.settings(), []);

  const [lots, setLots] = useState(() =>
    order.appraised?.length
      ? order.appraised.map((lot) => ({
          label: lot.label || "", metal: lot.metal, purity: lot.purity || "",
          grossWeight: String(lot.grossWeight ?? ""), stoneWeight: String(lot.stoneWeight ?? ""),
          testMethod: lot.testMethod || "acid", note: lot.note || "",
        }))
      : [blankLot()]
  );
  const [pricing, setPricing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [offering, setOffering] = useState(false);
  const [offerNote, setOfferNote] = useState("");
  const [manualAmount, setManualAmount] = useState("");

  // Price as the appraiser types, debounced so a scale reading being typed in
  // does not fire six requests.
  // Every row is priced, including empty ones, so a priced result lines up with
  // its row by index. Saving drops the empty rows instead.
  const payload = useMemo(
    () => lots.map((lot) => ({
      ...lot,
      grossWeight: Number(lot.grossWeight) || 0,
      stoneWeight: Number(lot.stoneWeight) || 0,
    })),
    [lots]
  );
  const savable = useMemo(() => payload.filter((lot) => lot.grossWeight > 0), [payload]);
  const debouncedPayload = useDebounced(JSON.stringify(payload), 400);

  useEffect(() => {
    const parsed = JSON.parse(debouncedPayload);
    if (!parsed.some((lot) => lot.grossWeight > 0)) { setPricing(null); return undefined; }

    let alive = true;
    ordersApi.priceAppraisal(order.number, parsed)
      .then((result) => { if (alive) setPricing(result); })
      .catch(() => {});
    return () => { alive = false; };
  }, [debouncedPayload, order.number]);

  const update = useCallback((index, next) => {
    setLots((list) => list.map((lot, i) => (i === index ? next : lot)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const result = await ordersApi.saveAppraisal(order.number, savable);
      toast.success(`Saved ${result.pricing.lots.length} lots`);
      onSaved(result.order);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const sendOffer = async () => {
    setSaving(true);
    try {
      await ordersApi.saveAppraisal(order.number, savable);
      const updated = await ordersApi.makeOffer(order.number, {
        amount: manualAmount === "" ? undefined : Number(manualAmount),
        note: offerNote || undefined,
        notify: true,
      });
      toast.success("Offer sent to the customer");
      setOffering(false);
      onSaved(updated);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const total = pricing?.pricing?.total ?? 0;

  // What the referral would cost at this offer. The same rule the server applies
  // when the payout is recorded: a percentage of what the customer is paid,
  // never more than the cap, and a cap of zero means no cap.
  const terms = order.affiliate?.commission;
  const projectedCommission = terms?.percent > 0
    ? Math.min(
        Number((total * (terms.percent / 100)).toFixed(2)),
        terms.cap > 0 ? terms.cap : Number.POSITIVE_INFINITY
      )
    : 0;

  return (
    <div className="space-y-5">
      {pricing?.spot?.stale && (
        <Alert tone="warning" title="The metal price feed is stale">
          These amounts are calculated from a price that has not updated. Refresh it in settings before quoting.
        </Alert>
      )}

      {/* One lot per card under 1024px, one per row above it. The switch is at
          lg rather than sm because ten columns need about 900 pixels before the
          weight fields become too narrow to type into. */}
      <ul className="lg:hidden space-y-3">
        {lots.map((lot, index) => (
          <LotCard
            key={index}
            index={index}
            lot={lot}
            priced={pricing?.pricing?.lots?.[index]}
            purities={settings?.purities}
            onChange={(next) => update(index, next)}
            onRemove={() => setLots(lots.filter((_, i) => i !== index))}
          />
        ))}
      </ul>

      <Panel className="overflow-hidden max-lg:border-0 max-lg:bg-transparent">
        <div className="overflow-x-auto hidden lg:block">
          <table className="w-full min-w-[900px] text-[13px]">
            <thead>
              <tr className="border-b border-rule">
                {["Description", "Metal", "Purity", "Gross g", "Stones g", "Test", "Fine", "Rate", "Amount", ""].map((heading, index) => (
                  <th
                    key={heading || index}
                    className={`px-2 py-2.5 eyebrow font-semibold ${index >= 6 ? "text-right" : "text-left"}`}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lots.map((lot, index) => (
                <LotRow
                  key={index}
                  lot={lot}
                  priced={pricing?.pricing?.lots?.[index]}
                  purities={settings?.purities}
                  onChange={(next) => update(index, next)}
                  onRemove={() => setLots(lots.filter((_, i) => i !== index))}
                />
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-0 lg:px-3 py-3 lg:py-2.5 border-t border-rule-soft max-lg:border-t-0">
          <Button variant="quiet" size="sm" onClick={() => setLots([...lots, blankLot()])} className="max-lg:w-full">
            <Plus size={15} /> Add lot
          </Button>
          <p className="hint num">
            {pricing?.spot?.perGram
              ? Object.entries(pricing.spot.perGram)
                  .filter(([, value]) => value > 0)
                  .map(([metal, value]) => `${METAL_LABELS[metal]} ${moneyExact(value)}/g`)
                  .join("  ·  ")
              : ""}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 px-4 sm:px-5 py-5 bg-surface border border-rule lg:border-0 lg:border-t">
          <div>
            <p className="eyebrow">Gross</p>
            <p className="display display-sm text-ink mt-1 num">
              {grams(payload.reduce((sum, lot) => sum + lot.grossWeight, 0))}
            </p>
          </div>
          <div>
            <p className="eyebrow">Fine metal</p>
            <p className="display display-sm text-ink mt-1 num">
              {pricing ? Object.entries(pricing.pricing.fineByMetal).map(([metal, value]) => `${value.toFixed(2)}g ${metal}`).join(", ") || "—" : "—"}
            </p>
          </div>
          <div>
            <p className="eyebrow">Melt value</p>
            <p className="display display-sm text-ink mt-1 num">{pricing ? money(pricing.pricing.totalMelt) : "—"}</p>
          </div>
          <div className="sm:text-right">
            <p className="eyebrow">Calculated offer</p>
            <p className="display display-md text-ink mt-1 num">{pricing ? money(total) : "—"}</p>
          </div>
        </div>

        {/* The one screen where the affiliate actually changes the arithmetic, so
            it says what this offer costs in commission at the moment somebody is
            deciding what to quote. A projection until the payout is recorded,
            and it says so. */}
        {order.affiliate?.code && total > 0 && (
          <div className="flex flex-wrap items-baseline justify-between gap-3 px-4 sm:px-5 py-3 border-l-2 border-bronze bg-bronze-wash">
            <p className="text-[13px] text-bronze-dark">
              <span className="font-semibold">{order.affiliate.name}</span> earns{" "}
              {order.affiliate.commission?.percent || 0}% of the payout
              {order.affiliate.commission?.cap > 0 ? `, capped at ${money(order.affiliate.commission.cap)}` : ""}
            </p>
            <p className="num text-[14px] font-semibold text-bronze-dark">
              {money(projectedCommission)} on this offer
            </p>
          </div>
        )}
      </Panel>

      {pricing?.pricing?.belowMinimum && (
        <Alert tone="warning" title="Under the minimum offer">
          This works out below the minimum in settings. Decide by hand whether to quote it or send it back.
        </Alert>
      )}

      <div className="flex flex-col-reverse sm:flex-row flex-wrap gap-2">
        <Button variant="quiet" onClick={save} loading={saving} disabled={!savable.length} className="max-sm:w-full">
          <Check size={16} /> Save appraisal
        </Button>
        <Button onClick={() => { setManualAmount(""); setOffering(true); }} disabled={!savable.length || total <= 0} className="max-sm:w-full">
          <Scale size={16} /> Send offer of {money(total)}
        </Button>
        {order.offer?.amount && (
          <span className="hint self-center ml-2">
            Current offer {money(order.offer.amount)}{order.offer.response ? `, ${order.offer.response}` : ", awaiting a reply"}
          </span>
        )}
      </div>

      <Modal
        open={offering}
        onClose={() => setOffering(false)}
        title="Send the offer"
        description={`${order.customer?.firstName} will get an email${order.consent?.sms ? " and a text" : ""} with the itemised breakdown.`}
        footer={
          <>
            <Button variant="quiet" onClick={() => setOffering(false)}>Cancel</Button>
            <Button onClick={sendOffer} loading={saving}>
              Send {money(manualAmount === "" ? total : Number(manualAmount))}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Panel quiet className="p-4">
            <div className="flex items-baseline justify-between">
              <span className="hint">Calculated from the weights</span>
              <span className="display display-sm num text-ink">{money(total)}</span>
            </div>
          </Panel>

          <Input
            label="Override the amount"
            type="number"
            min="0"
            step="1"
            prefix="$"
            className="pl-7"
            placeholder={String(total)}
            hint="Leave blank to send the calculated figure. An override is recorded in the log."
            value={manualAmount}
            onChange={(event) => setManualAmount(event.target.value)}
          />

          <Textarea
            label="Note to the customer"
            rows={3}
            placeholder="Optional. This appears above the breakdown on their page, so use it to explain anything unusual."
            value={offerNote}
            onChange={(event) => setOfferNote(event.target.value)}
          />

          <Alert tone="info">
            <Refresh size={14} className="inline mr-1.5 -mt-0.5" />
            The offer is held for the number of days set in settings, then requoted free at the price on the day.
          </Alert>
        </div>
      </Modal>
    </div>
  );
}
